<template>
  <div class="pay-plan-row-menu">
    <MenuIcon
      v-if="menuItems.length > 0"
      :dropdownItems="menuItems"
      :isOpen="isOpen"
      @onClick="handleMenuClick"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import MenuIcon from '@components/MenuIcon/MenuIcon.vue';

const props = defineProps({
  status: {
    type: String,
    required: true,
    validator: (value) => ['Proposed', 'In Force', 'Terminated', 'Voided'].includes(value)
  },
  approvalStatus: {
    type: String,
    default: 'Draft',
    validator: (value) => ['Draft', 'Pending approval', 'Approved', 'Rejected'].includes(value)
  },
  terminable: {
    type: Boolean,
    default: false
  },
  voidable: {
    type: Boolean,
    default: false
  },
  voidBlockedBy: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['action']);

const isOpen = ref(false);

// Action metadata per B-19 rule (updated by Eric's 2026-10-06 comment)
const ACTION_META = {
  edit: {
    name: 'Edit',
    value: 'edit',
    icon: 'icons/dropdown/rename.svg',
    destructive: false
  },
  submit: {
    name: 'Submit',
    value: 'submit',
    icon: 'icons/dropdown/activate.svg',
    destructive: false
  },
  delete: {
    name: 'Delete',
    value: 'delete',
    icon: 'icons/dropdown/trash-01.svg', // Untitled UI trash-01 (R6)
    destructive: true,
    negative: true
  },
  resetToDraft: {
    name: 'Reset to draft',
    value: 'reset_to_draft',
    icon: 'icons/dropdown/reverse-left.svg', // Untitled UI reverse-left (R12)
    destructive: false
  },
  void: {
    name: 'Void',
    value: 'void',
    icon: 'icons/dropdown/archive-new.svg',
    destructive: true,
    negative: true
  },
  terminate: {
    name: 'Terminate',
    value: 'terminate',
    icon: 'icons/dropdown/archive-new.svg',
    destructive: true,
    negative: true
  }
};

// Compute menu items based on status and approval status per B-19
const menuItems = computed(() => {
  const items = [];

  if (props.status === 'Proposed') {
    if (props.approvalStatus === 'Draft') {
      // Draft: Edit, Submit, Delete
      items.push(ACTION_META.edit);
      items.push(ACTION_META.submit);
      items.push(ACTION_META.delete);
    } else if (props.approvalStatus === 'Pending approval') {
      // Pending approval: Reset to draft only (per Eric's comment)
      items.push(ACTION_META.resetToDraft);
    } else if (props.approvalStatus === 'Rejected') {
      // Rejected: Void only
      items.push(ACTION_META.void);
    }
    // Approved: no menu items (should transition to In Force)
  } else if (props.status === 'In Force') {
    // In Force: Terminate (if terminable), Void (if voidable)
    if (props.terminable) {
      items.push(ACTION_META.terminate);
    }
    if (props.voidable || props.voidBlockedBy) {
      const voidItem = { ...ACTION_META.void };
      if (props.voidBlockedBy) {
        voidItem.disabled = true;
        // Rule A-9: versions are voided newest first — name the blocker.
        voidItem.disabledTooltip = `Void ${props.voidBlockedBy} first — versions are voided newest first`;
      }
      items.push(voidItem);
    }
  }
  // Terminated or Voided: no menu items

  return items;
});

function handleMenuClick(item) {
  isOpen.value = false;
  emit('action', item.value);
}
</script>

<style scoped>
.pay-plan-row-menu {
  display: inline-flex;
  align-items: center;
}
</style>
